import { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import Badge from 'components/common/Badge';
import useMartyContext from 'hooks/useMartyContext';
import Link from 'components/common/MartyLink';
import { fetchProductDetail } from 'actions/productDetail';
import { makeAscii } from 'helpers';
import TrustedRetailerBanner from 'components/common/TrustedRetailerBanner';
import MelodyModal from 'components/common/MelodyModal';
import Card from 'components/common/card/Card';
import CardMedia from 'components/common/card/CardMedia';
import CardPrice from 'components/common/card/CardPrice';
import Rating from 'components/Rating';
import SponsoredBanner from 'components/common/SponsoredBanner';
import getCardData from 'data/getCardData';
import useHover from 'hooks/useHover';
import useCrossSite from 'hooks/useCrossSite';
import Hearts from 'components/common/Hearts';
import CrossSiteLabel from 'components/common/melodyCard/CrossSiteLabel';
import LowStockLabel from 'components/common/melodyCard/LowStockLabel';
import { SEARCH_IMGS_NOT_LAZY_LOADED } from 'constants/appConstants';
import { getCardFlag } from 'helpers/cardUtils';
import { HYDRA_SEARCH_PRODUCT_HOVER } from 'constants/hydraTests';
import { track } from 'apis/amethyst';
import { evBadgeCategoryClick } from 'events/product';
import { isAssigned } from 'actions/ab';
import ProductUtils from 'helpers/ProductUtils';
import useWindowSize from 'hooks/useWindowSize';
import Tag from 'tailwind/components/Tag/Tag';
import { isMobileViewport } from 'utils/viewportUtil';

// eslint-disable-next-line css-modules/no-unused-class
import css from 'styles/components/common/productCard.scss';
// eslint-disable-next-line css-modules/no-unused-class
import crossSiteProductsCSS from 'styles/components/search/crossSiteProducts.scss';

const ProductCard = props => {
  const {
    index,
    linkProps,
    className,
    brandName,
    intersectionRef,
    isSponsored,
    onClick,
    onMediaHovered,
    productName,
    productSeoUrl,
    productUrl,
    styleColor,
    storeName,
    styleId,
    CardDetailsTopSlot,
    CardDetailsTopSlot2,
    CardBottomSlot,
    onHand,
    productId,
    isSearchProductHover,
    imageClassName,
    isLinkDisabled = false,
    mediaChildren,
    showPrice = true,
    showColorName = false,
    makeStarsBlock,
    badges = [],
    isLaunchCalendarView = false,
    showRecosBadge = false,
    imageBadgeClassName,
    amethystRecoType,
    isMobileBlockLayout = false,
    showProductTag = false,
    productTagCount = 0,
    dateLabel,
    preLabel = '',
    includeSwatchRowSpace = false,
    isSponsoredAdCardSwatch = false
  } = props;

  const {
    testId,
    marketplace: {
      search: { showRatingStars, showLowStockLabel }
    }
  } = useMartyContext();
  const [ref, setRef] = useState(null);

  const { isHovered } = useHover(ref);
  const [badgeAccessibilityLabel, setBadgeAccessibilityLabel] = useState('');
  const { onClick: crossSiteClick, closeClick, content, visible } = useCrossSite(props);
  const displayRatingStars = isLaunchCalendarView ? false : showRatingStars;
  const { hearts, media, price, reviews } = getCardData({
    ...props,
    isHovered,
    showRatingStars: displayRatingStars
  });

  const finalProductSeoUrl = isSponsoredAdCardSwatch ? null : productSeoUrl; // prevent redirects to the wrong style on PDP from ads
  const productLink = finalProductSeoUrl || productUrl || null;
  const encodedBrandName = makeAscii(brandName);
  const encodedProductName = makeAscii(productName);
  const encodedPreLabel = makeAscii(preLabel);
  const { heartsData, ...restHeartsProps } = hearts;
  const { width: windowWidth = 0 } = useWindowSize();
  const isMobile = isMobileViewport(windowWidth);

  const clickHandler = e => {
    if (crossSiteClick) {
      crossSiteClick(e);
    } else {
      onClick?.(e, styleId);
    }
  };

  const flags = {
    'sponsored': <SponsoredBanner newCard={true} index={index} />,
    'trusted retailer': (
      <div className={css.isTrusted}>
        <TrustedRetailerBanner styleId={styleId} />
      </div>
    )
  };

  const flagType = getCardFlag({ ...props });
  const flag = flags[flagType] || null;
  const hasLowStock = onHand <= 10;
  const displayLowStockLabel = !isLaunchCalendarView && !!showLowStockLabel && hasLowStock;
  const productLabel = ProductUtils.getProductLabel(
    encodedProductName,
    encodedBrandName,
    styleColor,
    isSponsored,
    !!badges.length,
    displayLowStockLabel,
    encodedPreLabel
  );

  const modalProps = {
    onRequestClose: () => closeClick(),
    className: crossSiteProductsCSS.modal,
    contentLabel: 'Trusted Retailers',
    isOpen: !!visible
  };

  const makeStars = () => {
    if (!displayRatingStars) {
      return null;
    }

    return (
      <>
        <dt>Rating</dt>
        <dd>
          <Rating
            rating={reviews.roundedRating}
            additionalClasses={makeStarsBlock && css.pdpCardReviews}
            reviewCount={reviews.ratingCount}
            hasDisplayReviewCount={true}
          />
        </dd>
      </>
    );
  };

  const setBadgeLabelHandler = useCallback(
    label => {
      setBadgeAccessibilityLabel(label);
    },
    [setBadgeAccessibilityLabel]
  );

  const onBadgeCategoryClick = () => {
    track(() => [evBadgeCategoryClick, { badges, styleId, amethystRecoType }]);
  };
  return (
    <Card
      className={className}
      // For helping site merch collect styleIds https://github01.zappos.net/mweb/marty/pull/9699
      data-style-id={styleId}
      data-test-id={props['data-test-id']}
      data-low-stock={displayLowStockLabel}
      itemScope
      itemType="https://schema.org/Product"
      ref={intersectionRef}
    >
      {visible && <MelodyModal {...modalProps}>{content}</MelodyModal>}
      <Link
        className={cn(css.productLink, { [css.mobileBlock]: isMobileBlockLayout })}
        data-style-id={styleId}
        to={productLink || ''}
        itemProp="url"
        data-test-id={testId('searchResultLink')}
        onClick={clickHandler}
        innerRef={setRef}
        isLinkDisabled={isLinkDisabled}
        {...linkProps}
      >
        {badges.length > 0 && badgeAccessibilityLabel}
        {productLabel} {price.label}. {reviews.label}
      </Link>
      <CardMedia
        onHover={onMediaHovered}
        forceLoadIndex={SEARCH_IMGS_NOT_LAZY_LOADED}
        {...media}
        imageClassName={cn(imageClassName, imageBadgeClassName)}
        disableHoverImage={isSponsored}
      >
        <div className={css.top}>
          {!showRecosBadge && !flag && badges.length > 0 && (
            <Badge
              setLabel={setBadgeLabelHandler}
              id={badges[0].bid}
              category={badges[0].zc}
              url={badges[0].url}
              classNameWrapper={css.badge}
              productLink={productLink}
              onBadgeCategoryClick={onBadgeCategoryClick}
            />
          )}
          {showRecosBadge && badges.length > 0 && (
            <Badge
              setLabel={setBadgeLabelHandler}
              id={badges[0].bid}
              category={badges[0].zc}
              url={badges[0].url}
              classNameWrapper={css.badge}
              productLink={productLink}
              onBadgeCategoryClick={onBadgeCategoryClick}
            />
          )}
          {flag}
          {!!dateLabel && (
            <div className={cn(css.dateLabel, { [css.disableLink]: isLinkDisabled })}>
              <Tag size={isMobile ? 'medium' : 'large'} variant="white">
                {dateLabel}
              </Tag>
            </div>
          )}
          <Hearts {...restHeartsProps} heartsData={{ ...heartsData, ...{ isDisplayCount: false } }} />
        </div>
        {mediaChildren}
        <div className={css.bottom}>
          {storeName && (
            <div className={css.storeWrapper}>
              <CrossSiteLabel storeName={storeName} />
            </div>
          )}
          {displayLowStockLabel && <LowStockLabel onHand={onHand} />}
          {showProductTag && <div className={css.tagBadge}>{productTagCount}</div>}
        </div>
      </CardMedia>
      <div className={css.detailsWrapper}>
        {!!CardDetailsTopSlot && <CardDetailsTopSlot {...props} />}
        {!!CardDetailsTopSlot2 && <CardDetailsTopSlot2 {...props} />}
        {includeSwatchRowSpace && <span className={css.mimicSwatchWrapper} />}
        <Link
          className={cn(css.details, {
            [css.mobileBlock]: isMobileBlockLayout,
            [css.hideHover]: isSearchProductHover
          })}
          onClick={clickHandler}
          tabIndex={-1}
          to={productLink || ''}
          isLinkDisabled={isLinkDisabled}
        >
          <dl>
            <dt>Brand Name</dt>
            <dd className={css.mainText} itemProp="brand" itemScope itemType="https://schema.org/Brand" data-test-id={testId('brand')}>
              <span itemProp="name">{encodedBrandName}</span>
            </dd>
            <dt>Product Name</dt>
            <dd className={css.subText} itemProp="name" data-test-id={testId('productName')}>
              {encodedProductName}
            </dd>
            <dt>Color</dt>
            <dd className={showColorName ? css.colorText : css.colorName} itemProp="color" data-test-id={testId('colorName')}>
              {styleColor}
            </dd>
            {showPrice && <CardPrice {...price} isAvailable={onHand > 0} url={productLink} />}
            {makeStars()}
          </dl>
        </Link>
        <meta itemProp="sku" content={productId} />
        {!!CardBottomSlot && <CardBottomSlot {...props} onClick={clickHandler} />}
      </div>
    </Card>
  );
};

const mapDispatchToProps = {
  fetchProductDetail
};

const mapStateToProps = state => {
  const {
    headerFooter: { content },
    filters: { term }
  } = state;
  const isSearchProductHover = isAssigned(HYDRA_SEARCH_PRODUCT_HOVER, 1, state);

  return {
    term,
    content,
    isSearchProductHover
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCard);
